/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { ChangeEvent, useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import {
  TextField,
  Grid,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
  ListItem,
  ListItemText,
  Tooltip,
  Box,
  Collapse,
  Stack,
  Button,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { searchVehicleService } from '~/features/Vehicle/services/index'
import {
  useHookstate,
  createState,
  useState,
  Downgraded,
} from '@hookstate/core'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '~/components/ErrorFallback'
import InfiniteScroll from 'react-infinite-scroll-component'
import { validPlate } from '~/utils/validators'
import useOccurrenceState from '~/features/Occurrence/stores/OccurrenceState'
import useOccurrenceCommentState from '~/features/Occurrence/stores/OccurrenceCommentState'
import useSystemState from '~/features/System/stores/SystemState'
import { Visibility } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'

export interface VehicleListProps {
  readonly id: number
  readonly plate: string
  readonly created_at: Dayjs
  readonly occurrence_id: number
  readonly circunstance: {
    readonly id: number
    readonly name: string
  }
  readonly destination: {
    readonly id: number
    readonly name: string
  }
  readonly vehicle_type: {
    readonly id: number
    readonly name: string
  }
  readonly vehicle_model: {
    readonly id: number
    readonly name: string
  }
  readonly vehicle_color: {
    readonly id: number
    readonly name: string
  }
  readonly vehicle_brand: {
    readonly id: number
    readonly name: string
  }
  readonly occurrence: {
    readonly created_at: Dayjs
    readonly type: {
      readonly name: string
    }
    readonly dispatches: ReadonlyArray<{
      readonly id: number
      readonly code: string
      readonly dispatch_group_id: number
      readonly stationHasAccess: boolean
    }>
  }
}

const plateState = createState('')

export function useSearchVehicle() {
  const plate = useState(plateState)

  return {
    plate: () => plate,
  }
}

export default function SearchVehicle() {
  const { translate } = useLang()
  const { windowManager } = useSystemState()

  const plateExternal = useSearchVehicle().plate
  const plate = useHookstate('')
  const plateSearch = useHookstate('')
  const vehicles = useHookstate<readonly VehicleListProps[]>(
    windowManager().searchVehicle.dataSearchVehicle.attach(Downgraded).get() ??
      []
  )
  const vehiclesCount = useHookstate(0)
  const { syncOccurrenceEditAttendent } = useOccurrenceState()
  const { syncPreviousComments } = useOccurrenceCommentState()

  const [filtersOpen, setFiltersOpen] = React.useState(true)
  const [height, setHeight] = React.useState(335)

  useEffect(() => {
    if (filtersOpen) {
      setHeight(335)
    } else {
      const timer = setTimeout(() => {
        setHeight(425)
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [filtersOpen])

  useEffect(() => {
    plate.set(plateExternal().get())
    return () => {
      plateExternal().set('')
    }
  }, [])

  const nextData = async (reset = false) => {
    const pageQuery = reset ? 0 : Math.floor(vehicles.get().length / 10)
    searchVehicleService({
      plate: plateSearch.get(),
      page: pageQuery,
      limit: 10,
    }).then(({ data, rowsCount }) => {
      if (reset) {
        vehicles.set(data)
        vehiclesCount.set(rowsCount)
      } else {
        vehicles.merge(data)
        vehiclesCount.set(rowsCount)
      }
    })
  }

  const onClick = (id) => {
    syncOccurrenceEditAttendent(id)
    syncPreviousComments(id)
    window.location.pathname == '/dispatch'
      ? windowManager()['dispatchesResultSearches'].open.set(true)
      : windowManager().occurrence.open.set(true)
  }

  useEffect(() => {
    if (!plate.get()?.length) {
      windowManager()['dispatchesResultSearches'].open.set(false)
    }
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container width={'100%'}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pb={1}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 400 }}
            >
              {translate('Filters')}
            </Typography>
            <IconButton
              onClick={() => setFiltersOpen((prev) => !prev)}
              size="small"
              sx={{ 
                ml: 1,
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: filtersOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ExpandLess />
            </IconButton>
          </Stack>
          <Collapse 
            in={filtersOpen}
            timeout={{
              enter: 300,
              exit: 300
            }}
            easing={{
              enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.6, 1)'
            }}
          >
            <Grid container width={'100%'} direction="row" spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  inputProps={{ maxLength: 7 }}
                  label={translate('Plate')}
                  size="small"
                  value={plate.get() ?? ''}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    plate.set(event.target.value)
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
              >
                <Button
                  variant="text"
                  onClick={() => {
                    plate.set('')
                    plateSearch.set('')
                    vehicles.set([])
                    vehiclesCount.set(0)
                  }}
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    textDecorationLine: 'underline',
                    color: 'text.primary',
                    ':hover': {
                      bgcolor: 'transparent',
                      color: 'primary.main',
                    },
                  }}
                >
                  {translate('Clear filters')}
                </Button>
                <Tooltip title={translate('Search Vehicles')}>
                  <IconButton
                    onClick={() => {
                      plateSearch.set(plate.get())
                      nextData(true)
                    }}
                    disabled={!validPlate(plate.get())}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Divider />
          </Collapse>
        </Grid>

        {/* Lista de veículos */}
        <Grid item xs={12}>
          <InfiniteScroll
            dataLength={vehicles.value.length}
            next={() => nextData()}
            hasMore={
              vehicles.value.length != vehiclesCount.get() &&
              vehicles.value.length != 0
            }
            height={height}
            style={{
              height: height,
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            loader={<CircularProgress />}
          >
            {vehicles.value.length !== 0 ? (
              vehicles.get()?.map((items) => (
                <React.Fragment key={items.id}>
                  <ListItem style={{ paddingLeft: 5, paddingRight: 0 }}>
                    <ListItemText
                      primary={`OC: ${
                        items?.occurrence_id
                      } | ${items?.occurrence.dispatches?.map(
                        (item) => `\n\n${item.code}`
                      )}`}
                      secondary={
                        <>
                          <Typography variant="subtitle2">
                            {items?.occurrence.type.name}
                          </Typography>
                          <Typography variant="subtitle2">
                            {items?.circunstance?.name}
                          </Typography>
                          <Typography variant="subtitle2">
                            Destino Final: {items?.destination?.name}
                          </Typography>
                          <Typography variant="subtitle2">
                            Data da Ocorrência:{' '}
                            {dayjs(items?.occurrence.created_at).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                          </Typography>
                          <Typography variant="subtitle2">
                            Data do Registro:{' '}
                            {dayjs(items?.created_at).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                          </Typography>
                          <Typography variant="subtitle2">
                            {items?.plate} {items?.vehicle_type?.name}
                          </Typography>
                          <Typography variant="subtitle2">
                            {items?.vehicle_brand?.name}{' '}
                            {items?.vehicle_model?.name}
                            {items?.vehicle_color?.name
                              ? '- ' + items?.vehicle_color?.name
                              : ''}
                          </Typography>
                        </>
                      }
                    />
                    <Box
                      sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'start',
                      }}
                    >
                      <IconButton
                        aria-label="comment"
                        onClick={() => onClick(items?.occurrence_id)}
                        title="Visualizar"
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Divider sx={{ marginTop: 2 }} />
                </React.Fragment>
              ))
            ) : (
              <Grid
                item
                xs={12}
                style={{
                  height: height, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box',
                  transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Typography>{translate('No Vehicles')}</Typography>
              </Grid>
            )}
          </InfiniteScroll>
        </Grid>
      </Grid>
    </ErrorBoundary>
  )
}
